import React from 'react';
import {
  ArrowLeft,
  BellRing,
  LayoutDashboard,
  LoaderCircle,
  LogOut,
} from 'lucide-react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import { API_URL } from '../../../utils/api';
import {
  countOpenAlertsBySeverity,
  isReflectStaff,
  reflectStaffErrorMessage,
} from '../../../utils/reflectStaff';
import '../../../pages/reflect/StudentReflect.css';

export class ReflectStaffApiError extends Error {
  constructor(status, detail = '') {
    super(reflectStaffErrorMessage(status, detail));
    this.status = status;
    this.detail = detail;
  }
}

export const staffRequest = async (path, options = {}) => {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  let data = {};
  try {
    data = await response.json();
  } catch (_error) {
    data = {};
  }
  if (!response.ok) throw new ReflectStaffApiError(response.status, data.detail);
  return data;
};

export const ReflectStaffRoute = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="reflect-page flex min-h-screen items-center justify-center text-white">
        <LoaderCircle className="h-8 w-8 animate-spin text-cyan" aria-label="Checking sign-in" />
      </div>
    );
  }

  if (!isAuthenticated || !isReflectStaff(user)) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/admin/login?returnTo=${encodeURIComponent(returnTo)}`}
        replace
        state={{ returnTo }}
      />
    );
  }

  return <Outlet />;
};

const AlertBadge = ({ alerts }) => {
  const counts = countOpenAlertsBySeverity(alerts);
  if (!counts.total) return null;

  return (
    <span
      className={`inline-flex min-w-6 items-center justify-center rounded-full px-2 py-0.5 text-xs
        font-black ${counts.urgent ? 'bg-red-500 text-white' : 'bg-cyan text-black'}`}
      aria-label={`${counts.total} open care alerts${counts.urgent ? `, ${counts.urgent} urgent` : ''}`}
    >
      {counts.total}
    </span>
  );
};

export const StaffPageLoading = ({ line = 'Loading Reflect...' }) => (
  <div className="flex min-h-[45vh] flex-col items-center justify-center text-center">
    <img
      src="/pizzul/thinking.png"
      alt="Pizzul thinking"
      className="h-28 w-28 object-contain drop-shadow-[0_0_24px_rgba(0,255,221,0.16)]"
    />
    <LoaderCircle className="mt-3 h-7 w-7 animate-spin text-cyan" aria-hidden="true" />
    <p className="mt-3 font-bold text-white/65">{line}</p>
  </div>
);

export const StaffError = ({ children }) => children ? (
  <div
    role="alert"
    className="rounded-2xl border border-red-400/40 bg-red-500/10 px-4 py-3 font-bold text-red-100"
  >
    {children}
  </div>
) : null;

const roleLabel = (role) => ({
  facilitator: 'Teacher',
  counselor: 'LTBB lead',
  admin: 'LTBB lead',
  ltbb_admin: 'LTBB lead',
}[role] || 'Staff');

const ReflectStaffShell = ({
  alerts = [],
  backTo,
  backLabel = 'Back',
  children,
  title,
  subtitle,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <main className="reflect-page min-h-screen overflow-x-hidden bg-[#050607] text-white">
      <div className="reflect-orb reflect-orb-one" />
      <div className="reflect-orb reflect-orb-two" />
      <div className="relative z-10 mx-auto min-h-screen w-full max-w-7xl px-4 py-5 sm:px-7 lg:py-8">
        <header className="mb-8 border-b border-white/10 pb-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to="/admin/reflect"
              className="flex min-h-11 items-center gap-3 rounded-xl px-1 focus:outline-none
                focus-visible:ring-2 focus-visible:ring-cyan"
            >
              <img src="/pizzul/chill.png" alt="" className="h-11 w-11 object-contain" />
              <span>
                <span className="block font-heading text-lg font-black tracking-wide text-cyan">
                  REFLECT
                </span>
                <span className="block text-xs font-bold text-white/45">Staff studio</span>
              </span>
            </Link>
            <nav className="flex flex-wrap items-center justify-end gap-1 sm:gap-2" aria-label="Reflect staff">
              <Button asChild variant="ghost" className="min-h-11 rounded-full text-white hover:bg-white/10">
                <Link to="/admin/reflect">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Classes
                </Link>
              </Button>
              <Button asChild variant="ghost" className="min-h-11 rounded-full text-white hover:bg-white/10">
                <Link to="/admin/reflect/alerts">
                  <BellRing className="mr-2 h-4 w-4" />
                  Care inbox
                  <AlertBadge alerts={alerts} />
                </Link>
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={signOut}
                className="min-h-11 rounded-full text-white/65 hover:bg-white/10 hover:text-white"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </nav>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-white/45">
            <span>{user?.name || user?.email || 'Signed-in staff'}</span>
            <span>{roleLabel(user?.role)}</span>
          </div>
        </header>

        {backTo && (
          <Link
            to={backTo}
            className="mb-5 inline-flex min-h-11 items-center gap-2 rounded-full px-3 font-bold
              text-white/65 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2
              focus-visible:ring-cyan"
          >
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </Link>
        )}

        <div className="mb-7 max-w-4xl">
          <h1 className="font-heading text-3xl font-black leading-tight sm:text-5xl">{title}</h1>
          {subtitle && <p className="mt-2 text-base leading-relaxed text-white/60 sm:text-lg">{subtitle}</p>}
        </div>

        {children}
      </div>
    </main>
  );
};

export default ReflectStaffShell;
